import React from "react";
import Seo from "./Seo";

function Contributors() {
  const contributions = [
    {
      title: 'Open Source Projects',
      description: 'Security tools and utilities available on GitHub',
      items: ['PentestScan', 'DarkExpose SOC', 'TCMS'],
      link: 'https://github.com/xilpanda',
      linkText: 'View on GitHub'
    },
    {
      title: 'Technical Documentation',
      description: 'Guides, tutorials, and technical notes',
      items: ['Linux Administration', 'Security Playbooks', 'DevOps Practices'],
      link: '/learn',
      linkText: 'Browse Notes',
      internal: true
    },
    {
      title: 'Community Labs',
      description: 'Hands-on learning environments',
      items: ['Security Labs', 'Linux Labs', 'Networking Labs'],
      link: '/labs',
      linkText: 'Explore Labs',
      internal: true
    }
  ];

  const ways = [
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
        </svg>
      ),
      title: 'Report Issues',
      description: 'Found a bug or have a suggestion? Open an issue on GitHub.'
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
      title: 'Share Knowledge',
      description: 'Contribute articles, tutorials, or documentation improvements.'
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      ),
      title: 'Provide Feedback',
      description: 'Your feedback helps improve the quality of resources and tools.'
    }
  ];

  return (
    <div className="community-container">
      <Seo
        title="Community - LinuxSecOps"
        description="Open-source security community resources, active projects, contribution options, and collaboration channels."
        path="/community"
      />

      {/* Hero */}
      <section className="community-hero">
        <div className="community-hero-content">
          <span className="community-hero-badge">Open Source</span>
          <h1 className="community-hero-title">
            Community
            <span className="community-hero-accent">& Contributors</span>
          </h1>
          <p className="community-hero-description">
            Building security tools and sharing knowledge with the community. 
            All projects are open source and welcome contributions.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="community-content">
        {/* Contributions Grid */}
        <div className="community-section">
          <h2 className="community-section-title">Active Projects</h2>
          <div className="community-grid">
            {contributions.map((contrib, index) => (
              <div key={index} className="community-card">
                <h3>{contrib.title}</h3>
                <p>{contrib.description}</p>
                <ul className="community-card-list">
                  {contrib.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
                {contrib.internal ? (
                  <a href={contrib.link} className="community-card-link">
                    {contrib.linkText}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12"/>
                      <polyline points="12 5 19 12 12 19"/>
                    </svg>
                  </a>
                ) : (
                  <a href={contrib.link} target="_blank" rel="noopener noreferrer" className="community-card-link">
                    {contrib.linkText}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/>
                      <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* How to Contribute */}
        <div className="community-section">
          <h2 className="community-section-title">How to Contribute</h2>
          <div className="community-ways">
            {ways.map((way, index) => (
              <div key={index} className="community-way">
                <div className="community-way-icon">{way.icon}</div>
                <div className="community-way-content">
                  <h4>{way.title}</h4>
                  <p>{way.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact */}
        <div className="community-contact">
          <div className="community-contact-inner">
            <h3>Get in Touch</h3>
            <p>Questions, suggestions, or want to collaborate?</p>
            <div className="community-contact-links">
              <a href="mailto:linuxsecops@proton.me" className="community-contact-btn primary">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                linuxsecops@proton.me
              </a>
              <a href="https://github.com/xilpanda" target="_blank" rel="noopener noreferrer" className="community-contact-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
                GitHub
              </a>
              <a href="https://www.linkedin.com/in/sandro-radinkovic-18a8a35/" target="_blank" rel="noopener noreferrer" className="community-contact-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contributors;
